// Use case selection (stories 21-25): the Customer Journey "Alt 2" adoption grid made clickable.
// Rows Win deals / Grow pipeline / Data foundation (top to bottom, as designed), columns Crawl /
// Walk / Run, with the two Efficiency use cases as a fourth row. Unsized cards show but stay
// disabled. Sized to fit one viewport so selecting never needs a scroll.

import {
  USE_CASES,
  type GridRow,
  type MaturityTier,
  type UseCase,
} from '../../data/use-case-catalog';
import { needsCrawlWarning } from '../../flow/step-model';
import { calculatorActions, useCalculatorStore } from '../../state/calculator-store';
import { Ph } from '../Icon';
import { useCaseIconUrl } from './use-case-icons';

const GRID_ROWS: GridRow[] = ['Win deals', 'Grow pipeline', 'Data foundation'];
const GRID_TIERS: MaturityTier[] = ['Crawl', 'Walk', 'Run'];
const PHASE_LABEL: Record<string, string> = { Crawl: 'Phase 1: Crawl', Walk: 'Phase 2: Walk', Run: 'Phase 3: Run' };

function UseCaseCard({ useCase, selected }: { useCase: UseCase; selected: boolean }) {
  const icon = useCaseIconUrl(useCase.id);
  const sized = useCase.sized;
  return (
    <button
      type="button"
      className="vc3-uc"
      aria-pressed={sized ? selected : undefined}
      disabled={!sized}
      onClick={() => calculatorActions.toggleUseCase(useCase.id)}
    >
      {icon ? <img src={icon} alt="" /> : <span className="vc3-uc-icon-empty" aria-hidden />}
      <span className="vc3-uc-text">
        <span>{useCase.name}</span>
        {!sized && <span className="vc3-uc-note">Not sized yet</span>}
      </span>
      {sized && (
        <span className="vc3-uc-check" aria-hidden>
          <Ph name="check" size={12} bold />
        </span>
      )}
    </button>
  );
}

export function UseCaseSelectionStep() {
  const selected = useCalculatorStore((s) => s.selected);
  const selectedIds = selected.map((s) => s.id);
  const isSelected = (id: string) => selectedIds.includes(id);
  const efficiency = USE_CASES.filter((u) => u.tier === 'Efficiency');

  return (
    <section aria-labelledby="vc3-uc-h" className="vc3-sel">
      <div className="vc3-eyebrow">Step 2</div>
      <h1 id="vc3-uc-h" className="vc3-h1">
        Which use cases will you adopt?
      </h1>
      <p className="vc3-lede">
        The typical adoption path runs left to right. Pick the use cases you are committing to;
        each one gets its own page next.
      </p>

      <div className="vc3-grid-scroll">
        <div className="vc3-grid">
          <span />
          {GRID_TIERS.map((tier) => (
            <div key={tier} className="vc3-phase" data-tier={tier}>
              {PHASE_LABEL[tier]}
            </div>
          ))}
          {GRID_ROWS.map((row) => (
            <GridRowCells key={row} row={row} isSelected={isSelected} />
          ))}
          <div className="vc3-rowlabel vc3-rowlabel-eff">Efficiency</div>
          <div className="vc3-cell vc3-cell-eff">
            {efficiency.map((u) => (
              <UseCaseCard key={u.id} useCase={u} selected={isSelected(u.id)} />
            ))}
            <span className="vc3-eff-note">Cost savings alongside the adoption path</span>
          </div>
        </div>
      </div>

      {/* Always rendered with a reserved height, so the warning appearing or clearing never
          shifts the grid while the SE is clicking cards. */}
      <div className="vc3-sel-foot" aria-live="polite">
        <span className="vc3-selcount">
          {selected.length === 0
            ? 'No use cases selected yet.'
            : `${selected.length} use case${selected.length === 1 ? '' : 's'} selected.`}
        </span>
        {needsCrawlWarning(selectedIds) && (
          <span className="vc3-sel-warn" role="status">
            <Ph name="info" size={14} style={{ marginTop: 2, flexShrink: 0 }} />
            <span>
              No Crawl use case yet. The adoption path usually starts with the data foundation, so it&rsquo;s
              worth raising. You can continue as is.
            </span>
          </span>
        )}
      </div>
    </section>
  );
}

function GridRowCells({ row, isSelected }: { row: GridRow; isSelected: (id: string) => boolean }) {
  return (
    <>
      <div className="vc3-rowlabel">{row}</div>
      {GRID_TIERS.map((tier) => {
        const cards = USE_CASES.filter((u) => u.tier === tier && u.gridRow === row);
        if (cards.length === 0) return <div key={tier} className="vc3-cell vc3-cell-empty" aria-hidden />;
        return (
          <div key={tier} className="vc3-cell">
            {cards.map((u) => (
              <UseCaseCard key={u.id} useCase={u} selected={isSelected(u.id)} />
            ))}
          </div>
        );
      })}
    </>
  );
}
